// Map reviewed grid rows to the importSellerInventory mutation input. Only rows with no error-level
// issue are sent (warnings don't block); the grid stores everything as strings, so numbers are
// coerced and blanks become omitted (undefined) optional fields. The resolved ids/codes are taken
// from the row's resolved fields: product = platformProductId, location = location code,
// lengthInches = canonical inches.

import { UnitCode } from '~/graphql/graphql';
import type { SellerImportRowInput } from '~/graphql/graphql';
import { productById } from './catalog';
import { parseImportNumber } from './numberParse';
import type { GridRow } from './rows';

const UNIT_VALUES = new Set<string>(Object.values(UnitCode));

function num(value: unknown): number | undefined {
  return parseImportNumber(value) ?? undefined;
}

// Whole-number fields (lead time days). Validation already blocks fractional input, so rather than
// silently rounding a value that slipped through (3.6 -> 4), omit anything that isn't an integer.
function int(value: unknown): number | undefined {
  const n = num(value);
  return n !== undefined && Number.isInteger(n) ? n : undefined;
}

export function unitOrUndefined(value: unknown): UnitCode | undefined {
  const code = String(value ?? '')
    .trim()
    .toUpperCase();
  return UNIT_VALUES.has(code) ? (code as UnitCode) : undefined;
}

function listingUnit(row: GridRow): UnitCode | undefined {
  return (
    unitOrUndefined(row.unit) ??
    productById(String(row.product ?? ''))?.standardUnitsCode
  );
}

function quantity(
  value: unknown,
  unit: UnitCode | undefined
): { value: number; unit: UnitCode } | undefined {
  const parsed = num(value);
  return parsed === undefined || unit === undefined
    ? undefined
    : { value: parsed, unit };
}

/** True when the row has at least one error-level issue (warnings don't block import). */
export function hasError(row: GridRow): boolean {
  return Object.values(row.__issues).some((issue) => issue.level === 'error');
}

/** Rows eligible for import: no errors and the three required resolved fields present. */
export function importableRows(rows: GridRow[]): GridRow[] {
  return rows.filter(
    (r) => !hasError(r) && r.product && r.location && r.lengthInches
  );
}

/** Build the mutation input from the importable rows (drops error rows; omits blank optionals). */
export function toImportRows(rows: GridRow[]): SellerImportRowInput[] {
  return importableRows(rows).map((r) => {
    const unit = listingUnit(r);
    return {
      rowId: r.__id,
      platformProductId: String(r.product),
      lengthInches: Number(r.lengthInches),
      locationCode: String(r.location),
      unit,
      stock: quantity(r.stock, unit),
      leadTimeDays: int(r.leadTimeDays),
      replenishmentRatePerWeek: quantity(r.replenishmentRate, unit),
      minimumOrderQuantity: quantity(r.moq, unit),
      criticalStockLevel: quantity(r.criticalStock, unit),
      lowStockLevel: quantity(r.lowStock, unit),
    };
  });
}
